import React from 'react'
import { Advisor } from '../../../utils/types'
import { ExpandableSection } from '../../components/ExpandableSection/ExpandableSection'
import '../../styles/AdvisorCardsModule/AdvisorAdditionalInfo.scss'
import { useMatchScreen } from '../../../context/MatchScreenContext'
import { trackOptimizelyEvent } from '../../../utils/tracking/optimizely'
import { trackEvent } from '../../../utils/tracking/mixpanel'
import { formatPhone, getAdvisorType } from './advisorUtils'
import { useAdvisorCard } from './AdvisorCardContext'
import { isAdvisorData } from './advisorTypeGuards'
import { AdvisorDisclosure } from './AdvisorDisclosure'
import getSecLinkLabel from '../../../utils/getSecLinkLabel'

type WithContactInfo = { email?: string; phone?: string; secLink?: string }

type LinkType = 'email' | 'phone' | 'default'
type AdvisorLink = { label: string; href: string; type: LinkType }

const buildLinks = (advisor: Advisor): AdvisorLink[] => {
  const { email, phone, secLink } = advisor as Advisor & WithContactInfo
  const secProfileLink = secLink ?? advisor.sec_link
  return [
    email && { label: 'Click to email', href: `mailto:${email}`, type: 'email' as LinkType },
    phone && { label: formatPhone(phone), href: `tel:${phone}`, type: 'phone' as LinkType },
    advisor.firmCrd && {
      label: 'View Firm Disclosure Brochure',
      href: `https://adviserinfo.sec.gov/firm/brochure/${advisor.firmCrd}`,
      type: 'default' as LinkType
    },
    secProfileLink && {
      label: getSecLinkLabel(secProfileLink),
      href: secProfileLink,
      type: 'default' as LinkType
    }
  ].filter(Boolean) as AdvisorLink[]
}

export const AdvisorAdditionalInfo = () => {
  const { state: { pageKey } } = useMatchScreen()
  const { position, advisorData } = useAdvisorCard()

  const { ctaLabels } = useAdvisorCard()

  if (!isAdvisorData(advisorData)) return null

  const advisor = advisorData.advisor

  const links = buildLinks(advisor)
  if (links.length === 0) return null

  const basePayload = {
    advisorname: advisor.name,
    position,
    'Advisor Type': getAdvisorType(advisor.format),
    page_key: pageKey
  }

  const handleEmailClick = () => {
    const eventKey = 'falc-handoff-email-click'
    trackOptimizelyEvent(eventKey, basePayload)
    trackEvent('MatchScreen', eventKey, basePayload)
  }
  const handlePhoneClick = () => {
    const eventKey = 'handoff-advisor-phone-click'
    trackOptimizelyEvent(eventKey, basePayload)
    trackEvent('MatchScreen', eventKey, basePayload)
  }

  const handleToggle = () => {
    const eventKey = 'show-more-details'
    const eventPayload = { page_key: pageKey, 'advisor-details': advisor.name, Position: position }
    trackOptimizelyEvent(eventKey, eventPayload)
    trackEvent('MatchScreen', eventKey, eventPayload)
  }

  const clickHandlers: Record<LinkType, (() => void) | undefined> = {
    email: handleEmailClick,
    phone: handlePhoneClick,
    default: undefined
  }

  return (
        <ExpandableSection label={ctaLabels.additionalInformation} variant='accordion' className='advisor-additional-info'
                           onToggle={handleToggle}>
            <ul className='advisor-additional-info__links'>
                {links.map(({ label, href, type }) => (
                    <li key={href}>
                        <a href={href} target='_blank' rel='noreferrer' onClick={clickHandlers[type]}>{label}</a>
                    </li>
                ))}
            </ul>
          {advisor.disclosure && <AdvisorDisclosure disclosure={advisor.disclosure} />}
        </ExpandableSection>
  )
}
